import React, { useEffect, useState } from 'react';
import { PageHeader, EmptyState } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api, downloadPdf, downloadFile } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { FileText, Download, FileJson, FileSpreadsheet } from 'lucide-react';
import { RiskBadge } from '@/components/common/RiskBadge';
import { toast } from 'sonner';

export default function ReportsPage() {
  const [assets, setAssets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    api.get('/assets').then(r => setAssets(r.data));
    api.get('/alerts', { params: { limit: 100 } }).then(r => setAlerts(r.data));
    api.get('/incidents', { params: { limit: 100 } }).then(r => setIncidents(r.data));
  }, []);

  const handleDownload = async (kind, id, filename) => {
    const key = `${kind}-${id}`;
    setDownloading(key);
    try {
      await downloadPdf(`/reports/${kind}/${id}/pdf`, filename);
    } catch (err) {
      toast.error('Unable to download report. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const handleExport = async (kind, format) => {
    const key = `${kind}-${format}`;
    setExporting(key);
    try {
      const ts = new Date().toISOString().slice(0, 10);
      const ext = format === 'json' ? 'json' : 'csv';
      const paths = { alerts: '/alerts/export', incidents: '/incidents/export', detections: '/detections/export' };
      await downloadFile(paths[kind], `AakashDrishti_${kind}_${ts}.${ext}`, { format });
      toast.success(`Export ready: ${kind}.${ext}`);
    } catch (err) {
      toast.error('Unable to export. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader
        title="Reports"
        subtitle="Generate PDF reports for alerts, incidents and assets. CSV / JSON exports available for offline analysis."
      />

      {/* Data exports strip */}
      <Card className="p-4 mb-5 border-border" data-testid="exports-strip">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold shrink-0">Data exports</div>
          {['alerts', 'incidents', 'detections'].map((kind) => (
            <div key={kind} className="flex items-center gap-1.5 border border-border rounded-md pl-2.5 pr-1 py-1">
              <span className="text-xs capitalize font-medium">{kind}</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => handleExport(kind, 'csv')}
                disabled={exporting === `${kind}-csv`}
                data-testid={`export-${kind}-csv`}
              >
                <FileSpreadsheet size={14} className="mr-1" />
                {exporting === `${kind}-csv` ? 'Preparing…' : 'CSV'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => handleExport(kind, 'json')}
                disabled={exporting === `${kind}-json`}
                data-testid={`export-${kind}-json`}
              >
                <FileJson size={14} className="mr-1" />
                {exporting === `${kind}-json` ? 'Preparing…' : 'JSON'}
              </Button>
            </div>
          ))}
          <div className="text-[11px] text-muted-foreground ml-auto max-w-md text-right">
            Exports include only records belonging to your organisation.
          </div>
        </div>
      </Card>

      <Tabs defaultValue="asset">
        <TabsList>
          <TabsTrigger value="asset" data-testid="tab-asset-reports">Asset monitoring</TabsTrigger>
          <TabsTrigger value="alert" data-testid="tab-alert-reports">Alert reports</TabsTrigger>
          <TabsTrigger value="incident" data-testid="tab-incident-reports">Incident reports</TabsTrigger>
        </TabsList>

        <TabsContent value="asset">
          <Card className="p-0 divide-y divide-border">
            {assets.length === 0 ? <EmptyState title="No assets" /> :
              assets.map(a => (
                <div key={a.id} className="flex items-center gap-3 p-3">
                  <FileText size={16} className="text-primary" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{a.name}</div>
                    <div className="text-xs text-muted-foreground">{a.location_name}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`asset-report-${a.id}`}
                    disabled={downloading === `asset-${a.id}`}
                    onClick={() => handleDownload('asset', a.id, `AakashDrishti_Asset_${a.name.replace(/\s+/g, '_').slice(0, 24)}.pdf`)}
                  >
                    <Download size={14} className="mr-1" /> {downloading === `asset-${a.id}` ? 'Generating…' : 'PDF'}
                  </Button>
                </div>
              ))}
          </Card>
        </TabsContent>
        <TabsContent value="alert">
          <Card className="p-0 divide-y divide-border">
            {alerts.length === 0 ? <EmptyState title="No alerts" description="No active alerts to report on for this organisation." /> :
              alerts.map(a => (
                <div key={a.id} className="flex items-center gap-3 p-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.title}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</div>
                  </div>
                  <RiskBadge severity={a.severity} />
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`alert-report-${a.id}`}
                    disabled={downloading === `alert-${a.id}`}
                    onClick={() => handleDownload('alert', a.id, `AakashDrishti_Alert_${a.id.slice(0, 8)}.pdf`)}
                  >
                    <Download size={14} className="mr-1" /> {downloading === `alert-${a.id}` ? 'Generating…' : 'PDF'}
                  </Button>
                </div>
              ))}
          </Card>
        </TabsContent>
        <TabsContent value="incident">
          <Card className="p-0 divide-y divide-border">
            {incidents.length === 0 ? <EmptyState title="No incidents" description="No incidents to report on for this organisation." /> :
              incidents.map(i => (
                <div key={i.id} className="flex items-center gap-3 p-3">
                  <span className="font-mono text-xs w-24">{i.number}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{i.title}</div>
                    <div className="text-xs text-muted-foreground">{i.status} · {fmtDate(i.created_at)}</div>
                  </div>
                  <RiskBadge severity={i.severity} />
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`incident-report-${i.id}`}
                    disabled={downloading === `incident-${i.id}`}
                    onClick={() => handleDownload('incident', i.id, `AakashDrishti_Incident_${i.number || i.id.slice(0, 8)}.pdf`)}
                  >
                    <Download size={14} className="mr-1" /> {downloading === `incident-${i.id}` ? 'Generating…' : 'PDF'}
                  </Button>
                </div>
              ))}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
